import { useState, useEffect } from 'react';

const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionQuality, setConnectionQuality] = useState('good');
  const [isChecking, setIsChecking] = useState(false);
  // Function to check connection quality by making a test request
  const checkConnectionQuality = async () => {
    if (!navigator.onLine) {
      setConnectionQuality('offline');
      return;
    }

    setIsChecking(true);
    const startTime = Date.now();

    try {
      // Create a fetch with a timeout to avoid hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout

      // Try to fetch a small resource to test connection speed
      // Using a small image or favicon that's likely to be cached
      const response = await fetch('/favicon.ico?' + Date.now(), {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const endTime = Date.now();
      const responseTime = endTime - startTime;

      if (response.ok) {
        if (responseTime < 800) {
          setConnectionQuality('good');
        } else if (responseTime < 2000) {
          setConnectionQuality('fair');
        } else {
          setConnectionQuality('poor');
        }
      } else {
        setConnectionQuality('poor');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('Connection check timed out');
        setConnectionQuality('poor');
      } else {
        console.warn('Connection quality check failed:', error);
        // Check if we're actually offline
        if (!navigator.onLine) {
          setConnectionQuality('offline');
        } else {
          setConnectionQuality('poor');
        }
      }
    } finally {
      setIsChecking(false);
    }
  };
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      checkConnectionQuality();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setConnectionQuality('offline');
    };

    const handleVisibilityChange = () => {
      // Check connection when user returns to the tab
      if (!document.hidden && navigator.onLine) {
        checkConnectionQuality();
      }
    };

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial connection quality check
    if (navigator.onLine) {
      checkConnectionQuality();
    }

    // More frequent connection quality checks (every 10 seconds)
    const frequentInterval = setInterval(() => {
      if (navigator.onLine && !document.hidden) {
        checkConnectionQuality();
      }
    }, 10000);

    // Additional check for connection quality changes (every 5 seconds when connection is poor)
    const qualityMonitorInterval = setInterval(() => {
      if (navigator.onLine && !document.hidden && (connectionQuality === 'poor' || connectionQuality === 'fair')) {
        checkConnectionQuality();
      }
    }, 5000);

    // Very frequent checks for offline detection (every 2 seconds)
    const offlineCheckInterval = setInterval(() => {
      if (!navigator.onLine && isOnline) {
        setIsOnline(false);
        setConnectionQuality('offline');
      } else if (navigator.onLine && !isOnline) {
        setIsOnline(true);
        checkConnectionQuality();
      }
    }, 2000);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(frequentInterval);
      clearInterval(qualityMonitorInterval);
      clearInterval(offlineCheckInterval);
    };
  }, [connectionQuality, isOnline]);

  return {
    isOnline,
    connectionQuality,
    isChecking,
    checkConnectionQuality
  };
};

export default useNetworkStatus;
